import { useState, useEffect, useCallback } from 'react';
import { toast } from '../utils/toast';

/**
 * V3-native itinerary data hook.
 *
 * Fetches /api/itineraries/:id and returns the V3 engine output directly
 * when engineVersion === 'V3'. No heavy normalization — the V3 schema is
 * already well-structured with days[].items[] and rich metadata.
 *
 * Falls back to null for non-V3 itineraries so the caller can delegate
 * to the legacy useItineraryData hook.
 */
export default function useItineraryV3(tripId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const applyPayload = useCallback((raw) => {
    const wrapper = raw?.data || raw || {};
    const itinerary =
      wrapper.itinerary && typeof wrapper.itinerary === 'object'
        ? wrapper.itinerary
        : null;

    if (!itinerary || itinerary.engineVersion !== 'V3') {
      // Not a V3 itinerary — caller should fall back to legacy hook
      setData(null);
      return;
    }

    const questionnaire =
      wrapper.QuestionnaireData ??
      (typeof wrapper.questionnairedata === 'string'
        ? JSON.parse(wrapper.questionnairedata)
        : wrapper.questionnairedata) ??
      itinerary.questionnaireData ??
      {};

    setData({
      engineVersion: 'V3',
      days: itinerary.days || [],
      tripIntent: itinerary.tripIntent || {},
      magicMessages: itinerary.magicMessages || {
        arrival: 'Welcome to Disney!',
        departure: 'Safe travels!',
      },
      tripWarnings: itinerary.tripWarnings || [],
      tripNotices: itinerary.tripNotices || [],
      ledger: itinerary.ledger || {},
      buildStamp: itinerary.buildStamp || {},
      opsDateISO: itinerary.opsDateISO || '',

      // Wrapper-level metadata
      tripName: wrapper.TripName || wrapper.tripname || 'Disney Trip',
      tripId: wrapper.TripId || wrapper.tripid || tripId,
      itineraryId: wrapper.ItineraryId || wrapper.itineraryid || '',
      createdAt: wrapper.CreatedAt || wrapper.createdat || '',
      questionnaire,
    });
  }, [tripId]);

  const fetchItinerary = useCallback(
    async (opts = {}) => {
      if (!tripId) return;
      const cacheBuster = opts.cacheBuster ?? Date.now();
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/itineraries/${tripId}?_t=${cacheBuster}`,
          { credentials: 'include', cache: 'no-cache' },
        );
        if (!res.ok) {
          if (res.status === 404)
            throw new Error('Itinerary not found. It may not exist or you may not have access.');
          if (res.status === 403)
            throw new Error("You don't have permission to view this itinerary.");
          throw new Error(`Failed to load itinerary (${res.status})`);
        }
        const raw = await res.json();
        applyPayload(raw);
      } catch (e) {
        setError(e.message || 'Failed to load itinerary');
        console.error('useItineraryV3 error:', e);
        if (!opts.silent) toast.error(e.message || 'Failed to load itinerary');
      } finally {
        setLoading(false);
      }
    },
    [tripId, applyPayload],
  );

  useEffect(() => {
    fetchItinerary();
  }, [fetchItinerary]);

  return {
    data,
    isV3: data?.engineVersion === 'V3',
    loading,
    error,
    refetch: (opts) => fetchItinerary({ cacheBuster: Date.now(), silent: true, ...opts }),
  };
}
